import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4kocn0e5n6i6fi7k9q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email }).toString(),
      });

      if (response.ok) {
        setSubmitMessage(t('footer.successMessage'));
        setEmail('');
      } else {
        setSubmitMessage(t('footer.errorMessage'));
      }
    } catch (error) {
      setSubmitMessage(t('footer.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-teal-500 to-purple-600 mx-6 mb-6 rounded-t-[32px] text-white">
      <div className="max-w-7xl mx-auto px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
          <div>
            <h3 className="text-5xl font-thin mb-6 leading-tight">
              {t('footer.title')}<br />{t('footer.titleLine2')}
            </h3>
            <p className="text-white/80 text-base mb-8 leading-relaxed">
              {t('footer.subtitle')}
            </p>
            
            <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form">
              <input
                type="email"
                name="email"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b-2 border-white text-white placeholder-white/60 py-3 mb-5 focus:outline-none focus:border-white/80 text-base"
                required
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-10 py-3 bg-white text-gray-900 rounded-full font-semibold hover:bg-white/90 transition-all inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? t('footer.subscribing') : t('footer.subscribeButton')}
                <i className="ri-arrow-right-line"></i>
              </button>
              {submitMessage && (
                <p className={`mt-3 text-sm ${submitMessage.includes(t('footer.successMessage')) ? 'text-green-200' : 'text-red-200'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wider mb-6 font-semibold">{t('footer.contactTitle')}</h4>
            <div className="space-y-3 mb-8">
              <p className="text-lg">WA: +52 8 132724843</p>
              <p className="text-lg">support@umigo.cc</p>
            </div>
            
            <h4 className="text-sm uppercase tracking-wider mb-4 font-semibold">{t('footer.officeTitle')}</h4>
            <p className="text-base text-white/80 leading-relaxed whitespace-pre-line">
              {t('footer.officeAddress')}
            </p>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-6">
            <a href="https://www.facebook.com/61584474026416" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center hover:opacity-80 transition-all">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center hover:opacity-80 transition-all">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center hover:opacity-80 transition-all">
              <i className="ri-tiktok-fill text-2xl"></i>
            </a>
          </div>

          <div className="text-sm text-white/70">
            {t('footer.copyright')}
          </div>

          <div className="flex items-center gap-5">
            <a href="/privacy" className="hover:underline text-base transition-all">{t('footer.privacy')}</a>
            <a href="/terms" className="hover:underline text-base transition-all">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
